import { useRef, useEffect, FC, useState, useCallback } from 'react';
import { TempusDominus, Namespace } from '@eonasdan/tempus-dominus';
import { format, isEqual } from 'date-fns';

import { KTSVG } from '../../../_metronic/helpers';
import clsx from 'clsx';


type Props = {
  className: string
  selected: Date | null
  onChange: (dt: Date | null) => void
  dateFormat? : string
  placeholderText? : string  
}

// https://18.react.dev/reference/react-dom/components/common#ref-callback
export const MonthSelector: FC<Props>  = ({className, selected, onChange, dateFormat = 'MMMM yyyy',  placeholderText = ''}) => {

  const [ selected_shift, setShift ] = useState('');

  const tempusRef = useRef<TempusDominus | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const subsRef = useRef<any>(null);
  const updateSubsRef = useRef<any>(null);

  const setRef = useCallback((node: HTMLInputElement | null) => {
    // cleanup
    if (inputRef.current) {
      if (tempusRef.current !== null) {    
        if (subsRef.current !== null) {          
          subsRef.current.unsubscribe();        
          subsRef.current = null;
        }
        // If you store multiple subscriptions, you'll need to unsubscribe both.
        // Tempus Dominus subscribe returns an object with an unsubscribe() method.
        if (updateSubsRef.current !== null) {
          updateSubsRef.current.unsubscribe();
          updateSubsRef.current = null;
        }
        tempusRef.current.dispose();
        tempusRef.current = null;                
      }            
    }
    
    // assign node
    inputRef.current = node;
    // setup tempusDominus
    if (node !== null) {
      if (node.dataset?.initdate) {
        node.value = node.dataset.initdate;
      }
    
      tempusRef.current = new TempusDominus(
        node,        
        {
          display: {
            viewMode: 'months',
            theme: 'light',
            components: {
              calendar: true,
              date: false,
              month: true,
              year: true,
              decades: false,
              clock: false,
              hours: false,
              minutes: false,
              seconds: false
            }
          },
          localization: {
            format: 'MMMM yyyy',
          }          
        }
      );     

      // 1. Existing change subscription
      if (onChange) {
        subsRef.current = tempusRef.current.subscribe(Namespace.events.change, (e: any) => {                
          onChange(e.isValid ? e.date : null);                
        });                    
      }

      // 2. NEW: Subscribe to the update event to catch month selection and auto-hide
      updateSubsRef.current = tempusRef.current.subscribe(Namespace.events.update, (e: any) => {
        // e.change will be 'M' when a month is clicked
        if (e.change === 'M') {
          tempusRef.current?.hide();
        }
      });
    }        
  }, [dateFormat, onChange]);


  const doClearDate = useCallback(() => {
    if (tempusRef.current) tempusRef.current.clear();    
    onChange(null);    
  }, [onChange]);


  // update input value
  useEffect(() => {        
    
    if (! selected) {
      if (inputRef.current) inputRef.current.value = '';
      doClearDate();
      return;
    }    
    
    let adjusted = new Date(selected);    
    
    if (inputRef.current) inputRef.current.value = format(adjusted,dateFormat);  
    if (! isEqual(selected, adjusted)) {                        
      onChange(adjusted);        
    }

    // detach reference to inputCurrent
    // eslint-disable-next-line
  }, [selected_shift, selected, dateFormat, doClearDate, onChange]);

  return (
  <div className='position-relative min-w-125px'>
    <input 
      ref={setRef}
      className={className + " pe-8"}
      type="text"            
      readOnly={true}      
      placeholder={placeholderText}      
      data-initdate={selected && dateFormat ? format(selected, dateFormat): undefined}
    />
    <div 
      className="position-absolute btn-active-warning btn btn-outline p-0 fs-10" 
      style={{top:'0.25rem', right: '1rem'}} 
      onClick={doClearDate} 
      title="Clear date"
    >
      <KTSVG path='/media/icons/duotune/arrows/arr097.svg' className='svg-icon-6 m-0 lh-0' />  
    </div>
  </div>  
  )
}